import {
  HttpClient,
  type HttpResponse,
  RobotoNotFoundException,
  type PaginatedResponse,
  robotoHeaders,
} from "@/shared/http/index.ts";
import {
  parseJsonWithBigIntTimestamps,
  parseValueCountIntegersAsBigInt,
} from "@/shared/json";
import { SearchQueryBody } from "@/types/search";

import {
  MetadataValuesRequest,
  MetadataValuesResponse,
  QualifiedRoboqlQuery,
  QueryRecord,
  SubmitRoboqlQueryRequest,
  SubmitStructuredQueryRequest,
  SubmitTermQueryRequest,
  ValueCounts,
} from "./QueryRecord.ts";

type QueryResultsPageOptions = Partial<{
  abortSignal: AbortSignal;
  nextPageToken: string;
  pageSize: number;
}>;

export class QueryService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  async convertNlpToRoboql(
    prompt: string,
    options?: Partial<{
      abortSignal: AbortSignal;
      orgId: string;
    }>,
  ): Promise<QualifiedRoboqlQuery> {
    const url = this.#httpClient.constructUrl("v1/query/convert/nlp-to-roboql");

    const response = await this.#httpClient.post(url, {
      body: JSON.stringify({ prompt }),
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
    });

    return response.json<QualifiedRoboqlQuery>();
  }

  /**
   * Parses a RoboQL string into a `SearchQueryBody` without executing
   * it. The returned spec is a **projection** — only fields the user
   * explicitly wrote in the RoboQL string are populated; everything
   * else stays `undefined`.
   *
   * Throws on parse failure (HTTP 400 mapped to a thrown error by
   * `httpClient.post`). Field-name validity is NOT checked here — that
   * happens at execution time, the same as for any other
   * `QuerySpecification`.
   */
  async convertRoboqlToSpec(
    query: string,
    options?: Partial<{
      abortSignal: AbortSignal;
      orgId: string;
    }>,
  ): Promise<SearchQueryBody> {
    const url = this.#httpClient.constructUrl(
      "v1/query/convert/roboql-to-spec",
    );

    const response = await this.#httpClient.post(url, {
      body: JSON.stringify({ query }),
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
    });

    return response.json<SearchQueryBody>();
  }

  async submitStructured(
    request: SubmitStructuredQueryRequest,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<QueryRecord> {
    const url = this.#httpClient.constructUrl("v1/query/submit/structured");

    const response = await this.#httpClient.post(url, {
      body: JSON.stringify(request),
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });

    return response.json<QueryRecord>();
  }

  async submitTerm(
    request: SubmitTermQueryRequest,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<QueryRecord> {
    const url = this.#httpClient.constructUrl("v1/query/submit/term");

    const response = await this.#httpClient.post(url, {
      body: JSON.stringify(request),
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });

    return response.json<QueryRecord>();
  }

  async submitRoboql(
    request: SubmitRoboqlQueryRequest,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<QueryRecord> {
    const url = this.#httpClient.constructUrl("v1/query/submit/roboql");

    const response = await this.#httpClient.post(url, {
      body: JSON.stringify(request),
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });

    return response.json<QueryRecord>();
  }

  async getQueryRecord(
    queryId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<QueryRecord> {
    const url = this.#httpClient.constructUrl(`v1/query/id/${queryId}`);

    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });

    return response.json<QueryRecord>();
  }

  /**
   * Experimental: this method may change or be removed without notice.
   * Counts how many of a query's matching records hold each value of the
   * field named in the `value_counts` block the query was submitted with,
   * through `GET v1/query/id/{id}/value-counts`. An integer `value`,
   * `first_time` or `last_time` is read as a `bigint`, so a nanosecond time
   * keeps every digit.
   *
   * Throws `RobotoNotFoundException` for every 404 without reading the
   * body, whether the deployment does not serve this route or the server does
   * not find the query. The board's entity query executor lets it fail the query.
   */
  async getValueCounts(
    queryId: string,
    options?: Partial<{ abortSignal: AbortSignal }>,
  ): Promise<ValueCounts> {
    const url = this.#httpClient.constructUrl(
      `v1/query/id/${queryId}/value-counts`,
    );

    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });

    if (response.status === 404) {
      throw new RobotoNotFoundException({
        error_code: RobotoNotFoundException.name,
        message: `No value counts available for query ${queryId}`,
      });
    }

    return response.json<ValueCounts>(parseValueCountIntegersAsBigInt);
  }

  async getQueryResultCount(
    queryId: string,
    options?: Partial<{ abortSignal: AbortSignal }>,
  ): Promise<number> {
    const url = this.#httpClient.constructUrl(`v1/query/id/${queryId}/count`);

    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });
    await response.throwIfError();

    return Number.parseInt(await response.text());
  }

  /**
   * One page of a query's results. The platform's nanosecond timestamp
   * fields, such as `start_time` and `end_time`, are read as `bigint`
   * (`parseJsonWithBigIntTimestamps`); `created` and `modified` stay strings.
   */
  async getQueryResults<T>(
    queryId: string,
    options?: QueryResultsPageOptions,
  ): Promise<PaginatedResponse<T>> {
    const response = await this.#getQueryResultsResponse(queryId, options);
    return response.jsonParsedBy<PaginatedResponse<T>>(
      parseJsonWithBigIntTimestamps,
    );
  }

  async #getQueryResultsResponse(
    queryId: string,
    options: QueryResultsPageOptions | undefined,
  ): Promise<HttpResponse> {
    const searchParams = new URLSearchParams();

    if (options?.nextPageToken !== undefined) {
      searchParams.append("next_page_token", options.nextPageToken);
    }

    if (options?.pageSize !== undefined) {
      searchParams.append("page_size", options.pageSize.toString());
    }

    const url = this.#httpClient.constructUrl(
      `v1/query/id/${queryId}/results`,
      searchParams,
    );

    return this.#httpClient.get(url, { signal: options?.abortSignal });
  }

  async queryMetadataValues(
    request: MetadataValuesRequest,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<MetadataValuesResponse> {
    const url = this.#httpClient.constructUrl("v1/query/metadata/values");

    const response = await this.#httpClient.post(url, {
      body: JSON.stringify(request),
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });

    return response.json<MetadataValuesResponse>();
  }
}
